/**
 * Compare two tag names case-insensitively. `a ≠ b`, `a = á`, `a = A`.
 * @param {string} aa
 * @param {string} bb
 * @returns {number} A negative number if `a` occurs before `b`; positive if `a` occurs after `b`;
 * 0 if they are equivalent.
 */
export const tagCompare = (aa, bb) => aa.localeCompare(bb, {}, { sensitivity: 'base' });

/**
 * Get the index of a given tag within an array of tags.
 * @example
 * const tags = [ 'foo', 'bár', 'BAZ' ];
 * console.log(findTagIndex(tags, 'foo')); // 0
 * console.log(findTagIndex(tags, 'bar')); // 1
 * console.log(findTagIndex(tags, 'baz')); // 2
 * console.log(findTagIndex(tags, 'qux')); // -1
 * @param {string[]} tags array of tags
 * @param {string} tag a tag key
 * @returns {number} the index of the tag in the tags array, or -1 if it's not found.
 */
export const findTagIndex = (tags, tag) => tags.findIndex((tt) => tagCompare(tt, tag) === 0);

/**
 * Finds a 'tagged value' in an array of tags.
 * @example
 * const tags = [ 'foo:bar' ];
 * const value = findTaggedValue(tags, 'foo');
 * console.log(value); // bar
 * @param {string[]} tags array of tags
 * @param {string} key a tag key
 * @returns {string | undefined} the portion of the tag following the tag key and the delimiter.
 */
export const findTaggedValue = (tags, key) => {
  const keyDelim = `${key}:`;
  const len = keyDelim.length;
  const index = tags.findIndex((tt) => tagCompare(tt.substr(0, len), keyDelim) === 0);
  if (index < 0) return undefined;

  return tags[index].substring(len);
};

/**
 * Finds a 'tagged value' in an array of tags.
 * @example
 * const tags = [ 'foo:bar', 'foo:baz' ];
 * const value = findTaggedValues(tags, 'foo');
 * console.log(value); // bar
 * @param {string[]} tags array of tags
 * @param {string} key a tag key
 * @returns {string[]} an matched tag values, containing the portion of the tag following tag key and the delimiter.
 */
export const findTaggedValues = (tags, key) => {
  const keyDelim = `${key}:`;
  const len = keyDelim.length;

  return tags
    .filter((tt) => tagCompare(tt.substr(0, len), keyDelim) === 0)
    .map((tt) => tt.substring(len));
};
